(() => {
  class CollectionPagination {
    constructor() {
      this.abortController = null;
      this.container = null;
      this.generation = 0;
      this.isLoading = false;
      this.loadMoreLink = null;
      this.observer = null;
      this.pagination = null;
      this.retryButton = null;
      this.sentinel = null;
      this.spinner = null;
      this.stateKey = '';

      document.addEventListener('product-grid:updating', (event) => this.invalidate(event.detail?.stateKey));
      document.addEventListener('product-grid:updated', (event) => this.initialize(event.detail?.stateKey));
    }

    initialize(stateKey = window.location.search) {
      this.invalidate(stateKey);

      this.container = document.getElementById('ProductGridContainer');
      this.pagination = this.container?.querySelector('[data-collection-pagination]') || null;
      if (!this.pagination) return;

      this.loadMoreLink = this.pagination.querySelector('[data-load-more]');
      this.retryButton = this.pagination.querySelector('[data-pagination-retry]');
      this.sentinel = this.pagination.querySelector('[data-infinite-scroll-sentinel]');
      this.spinner = this.pagination.querySelector('[data-pagination-spinner]');

      this.loadMoreLink?.addEventListener('click', (event) => {
        event.preventDefault();
        this.loadNextPage();
      });
      this.retryButton?.addEventListener('click', () => this.loadNextPage());

      if (!this.loadMoreLink || !this.sentinel || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
        },
        { rootMargin: '0px 0px 1600px 0px' }
      );
      this.observer.observe(this.sentinel);
    }

    invalidate(stateKey = '') {
      this.generation += 1;
      this.stateKey = stateKey;
      this.isLoading = false;
      this.abortController?.abort();
      this.abortController = null;
      this.observer?.disconnect();
      this.observer = null;
    }

    async loadNextPage() {
      if (this.isLoading || !this.loadMoreLink?.href || !this.container?.isConnected) return;

      const requestGeneration = this.generation;
      const requestStateKey = this.stateKey;
      const requestUrl = new URL(this.loadMoreLink.href, window.location.origin);
      const sectionId = this.container.dataset.sectionId;
      if (!sectionId) return;

      requestUrl.searchParams.set('section_id', sectionId);
      this.isLoading = true;
      this.setLoadingState(true);
      this.abortController = new AbortController();

      try {
        const response = await fetch(requestUrl.toString(), { signal: this.abortController.signal });
        if (!response.ok) throw new Error(`Collection pagination failed with status ${response.status}`);

        const responseText = await response.text();
        if (requestGeneration !== this.generation || requestStateKey !== this.stateKey) return;

        const parsedDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceContainer = parsedDocument.getElementById('ProductGridContainer');
        if (!sourceContainer) throw new Error('Product grid was not found in the section response');

        this.mergeProductGroups(sourceContainer);
        this.updateNextPage(sourceContainer);
        this.clearError();
      } catch (error) {
        if (error.name !== 'AbortError' && requestGeneration === this.generation) {
          this.showError();
          console.error(error);
        }
      } finally {
        if (requestGeneration === this.generation) {
          this.isLoading = false;
          this.abortController = null;
          this.setLoadingState(false);
        }
      }
    }

    mergeProductGroups(sourceContainer) {
      const targetGroups = this.container.querySelector('.product-grid-groups');
      const sourceGroups = sourceContainer.querySelectorAll('.product-group');
      if (!targetGroups || !sourceGroups.length) return;

      const loadedHandles = new Set(
        Array.from(this.container.querySelectorAll('[data-product-handle]'), (card) => card.dataset.productHandle)
      );
      const touchedGroups = new Set();

      sourceGroups.forEach((sourceGroup) => {
        const newItems = Array.from(sourceGroup.querySelectorAll(':scope > ul > li.grid__item')).filter((item) => {
          const card = item.querySelector('[data-product-handle]');
          const handle = card?.dataset.productHandle;
          if (!handle || loadedHandles.has(handle)) return false;
          loadedHandles.add(handle);
          item.querySelectorAll('link[rel="stylesheet"]').forEach((stylesheet) => stylesheet.remove());
          return true;
        });

        if (!newItems.length) return;

        const tag = sourceGroup.dataset.tag;
        const targetGroup = Array.from(targetGroups.querySelectorAll(':scope > .product-group')).find(
          (group) => group.dataset.tag === tag
        );

        if (targetGroup) {
          const targetList = targetGroup.querySelector(':scope > ul');
          if (!targetList) return;
          const fragment = document.createDocumentFragment();
          newItems.forEach((item) => fragment.appendChild(item));
          targetList.appendChild(fragment);
          touchedGroups.add(targetGroup);
          return;
        }

        const sourceList = sourceGroup.querySelector(':scope > ul');
        if (!sourceList) return;
        sourceList.replaceChildren(...newItems);
        const sourceTitle = sourceGroup.previousElementSibling?.classList.contains('titulo-tag')
          ? sourceGroup.previousElementSibling
          : null;
        this.insertGroupInOrder(targetGroups, sourceTitle, sourceGroup);
        touchedGroups.add(sourceGroup);
      });

      touchedGroups.forEach((group) => this.sortGroupBySku(group));
    }

    insertGroupInOrder(container, title, group) {
      const groups = Array.from(container.querySelectorAll(':scope > .product-group'));
      const insertionPoint = groups.find((existingGroup) => this.compareGroups(group, existingGroup) < 0);
      const fragment = document.createDocumentFragment();
      if (title) fragment.appendChild(title);
      fragment.appendChild(group);

      if (insertionPoint) {
        const insertionTitle = insertionPoint.previousElementSibling?.classList.contains('titulo-tag')
          ? insertionPoint.previousElementSibling
          : insertionPoint;
        container.insertBefore(fragment, insertionTitle);
      } else {
        container.appendChild(fragment);
      }
    }

    compareGroups(first, second) {
      const firstOrder = Number(first.dataset.order || 99999);
      const secondOrder = Number(second.dataset.order || 99999);
      if (firstOrder !== secondOrder) return firstOrder - secondOrder;
      return (first.dataset.tag || '').localeCompare(second.dataset.tag || '', undefined, { sensitivity: 'base' });
    }

    sortGroupBySku(group) {
      const list = group.querySelector(':scope > ul');
      if (!list) return;

      const items = Array.from(list.querySelectorAll(':scope > li.grid__item'));
      items.sort((first, second) => {
        const firstCard = first.querySelector('[data-product-handle]');
        const secondCard = second.querySelector('[data-product-handle]');
        const firstSku = firstCard?.dataset.productSku || '';
        const secondSku = secondCard?.dataset.productSku || '';
        return firstSku.localeCompare(secondSku, undefined, { numeric: true, sensitivity: 'base' });
      });

      const fragment = document.createDocumentFragment();
      items.forEach((item) => fragment.appendChild(item));
      list.appendChild(fragment);
    }

    updateNextPage(sourceContainer) {
      const nextLink = sourceContainer.querySelector('[data-load-more]');
      const nextPageUrl = nextLink?.getAttribute('href');
      if (nextPageUrl) {
        this.loadMoreLink.setAttribute('href', nextPageUrl);
        return;
      }

      this.observer?.disconnect();
      this.loadMoreLink?.remove();
      this.sentinel?.remove();
      this.loadMoreLink = null;
      this.sentinel = null;
      this.removeEmptyGroups();
    }

    removeEmptyGroups() {
      this.container.querySelectorAll('.product-group').forEach((group) => {
        if (group.querySelector('li.grid__item')) return;
        const title = group.previousElementSibling;
        if (title?.classList.contains('titulo-tag')) title.remove();
        group.remove();
      });
    }

    setLoadingState(isLoading) {
      this.spinner?.classList.toggle('hidden', !isLoading);
      this.loadMoreLink?.setAttribute('aria-busy', String(isLoading));
    }

    showError() {
      this.observer?.disconnect();
      this.pagination?.querySelector('[data-pagination-error]')?.classList.remove('hidden');
    }

    clearError() {
      this.pagination?.querySelector('[data-pagination-error]')?.classList.add('hidden');
      if (this.observer && this.sentinel) this.observer.observe(this.sentinel);
    }
  }

  const pagination = new CollectionPagination();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => pagination.initialize(), { once: true });
  } else {
    pagination.initialize();
  }
})();
